import { useState, useEffect } from 'react';
import { X, Plus, Save } from 'lucide-react';
import type { CommentAutoReplyRule, CommentPlatform, CommentRuleTrigger } from '@/types/outreach';
import styles from './AutoReplyRuleEditor.module.css';

const EMPTY_RULE: Partial<CommentAutoReplyRule> = {
  platform: 'FACEBOOK',
  name: '',
  isActive: true,
  triggerType: 'KEYWORD',
  triggerKeywords: '[]',
  publicReplyTemplate: '',
  privateReplyTemplate: '',
  sendPrivateDm: false,
  useAiResponse: false,
  aiPrompt: '',
};

const PLATFORMS: CommentPlatform[] = ['FACEBOOK', 'INSTAGRAM', 'TIKTOK'];
const TRIGGER_TYPES: CommentRuleTrigger[] = ['KEYWORD', 'ALL', 'AI_CLASSIFIED'];
const TRIGGER_LABELS: Record<CommentRuleTrigger, string> = {
  KEYWORD: 'Keyword Match',
  ALL: 'All Comments',
  AI_CLASSIFIED: 'AI Classified',
};

interface AutoReplyRuleEditorProps {
  rule?: CommentAutoReplyRule | null;
  onSave: (
    rule: Partial<CommentAutoReplyRule> & { platform: CommentPlatform; name: string },
  ) => void;
  onCancel: () => void;
  isSaving: boolean;
}

export function AutoReplyRuleEditor({
  rule,
  onSave,
  onCancel,
  isSaving,
}: AutoReplyRuleEditorProps) {
  const [form, setForm] = useState<Partial<CommentAutoReplyRule>>(
    rule ? { ...rule } : { ...EMPTY_RULE },
  );
  const [keywordInput, setKeywordInput] = useState('');

  useEffect(() => {
    setForm(rule ? { ...rule } : { ...EMPTY_RULE });
    setKeywordInput('');
  }, [rule]);

  const keywords: string[] = (() => {
    try {
      return JSON.parse(form.triggerKeywords ?? '[]');
    } catch {
      return [];
    }
  })();

  const setKeywords = (kws: string[]) => {
    setForm((f) => ({ ...f, triggerKeywords: JSON.stringify(kws) }));
  };

  const addKeyword = () => {
    const kw = keywordInput.trim();
    if (!kw || keywords.includes(kw)) return;
    setKeywords([...keywords, kw]);
    setKeywordInput('');
  };

  const removeKeyword = (kw: string) => {
    setKeywords(keywords.filter((k) => k !== kw));
  };

  const handleSubmit = () => {
    if (!form.name || !form.platform) return;
    onSave(form as Partial<CommentAutoReplyRule> & { platform: CommentPlatform; name: string });
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h4 className={styles.title}>{form.id ? 'Edit Rule' : 'New Rule'}</h4>
        <button onClick={onCancel} className={styles.cancelBtn}>
          <X className={styles.cancelIcon} />
        </button>
      </div>

      <div className={styles.body}>
        <div className={styles.row2}>
          <label className={styles.label}>
            Platform
            <select
              value={form.platform ?? 'FACEBOOK'}
              onChange={(e) => setForm({ ...form, platform: e.target.value as CommentPlatform })}
              className={styles.select}
            >
              {PLATFORMS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
          <label className={styles.label}>
            Rule Name
            <input
              type="text"
              value={form.name ?? ''}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={styles.input}
              placeholder="e.g. Reply to product questions"
            />
          </label>
        </div>

        <label className={styles.label}>
          Trigger Type
          <div className={styles.triggerGroup}>
            {TRIGGER_TYPES.map((t) => (
              <button
                key={t}
                onClick={() => setForm({ ...form, triggerType: t })}
                className={`${styles.triggerBtn} ${form.triggerType === t ? styles.triggerBtnActive : ''}`}
              >
                {TRIGGER_LABELS[t]}
              </button>
            ))}
          </div>
        </label>

        {form.triggerType === 'KEYWORD' && (
          <label className={styles.label}>
            Trigger Keywords
            <div className={styles.keywordTags}>
              {keywords.map((kw) => (
                <span key={kw} className={styles.keywordTag}>
                  {kw}
                  <button onClick={() => removeKeyword(kw)} className={styles.tagRemove}>
                    <X className={styles.tagRemoveIcon} />
                  </button>
                </span>
              ))}
            </div>
            <div className={styles.keywordInputRow}>
              <input
                type="text"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                placeholder="Type keyword + Enter"
                className={styles.input}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addKeyword();
                  }
                }}
              />
              <button onClick={addKeyword} className={styles.addKwBtn}>
                <Plus className={styles.addKwIcon} />
              </button>
            </div>
          </label>
        )}

        <label className={styles.label}>
          Public Reply Template
          <textarea
            value={form.publicReplyTemplate ?? ''}
            onChange={(e) => setForm({ ...form, publicReplyTemplate: e.target.value })}
            className={styles.textarea}
            rows={3}
            placeholder="Public reply visible to all..."
          />
        </label>

        {(form.platform === 'FACEBOOK' || form.platform === 'INSTAGRAM') && (
          <label className={styles.label}>
            Private Reply Template
            <textarea
              value={form.privateReplyTemplate ?? ''}
              onChange={(e) => setForm({ ...form, privateReplyTemplate: e.target.value })}
              className={styles.textarea}
              rows={3}
              placeholder="Private DM text (FB/IG only)..."
            />
          </label>
        )}

        {form.useAiResponse && (
          <label className={styles.label}>
            AI Prompt
            <textarea
              value={form.aiPrompt ?? ''}
              onChange={(e) => setForm({ ...form, aiPrompt: e.target.value })}
              className={styles.textarea}
              rows={3}
              placeholder="Describe what the AI should do..."
            />
          </label>
        )}

        <div className={styles.checkboxRow}>
          <label className={styles.checkbox}>
            <input
              type="checkbox"
              checked={form.isActive ?? true}
              onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
            />
            Active
          </label>
          {(form.platform === 'FACEBOOK' || form.platform === 'INSTAGRAM') && (
            <label className={styles.checkbox}>
              <input
                type="checkbox"
                checked={form.sendPrivateDm ?? false}
                onChange={(e) => setForm({ ...form, sendPrivateDm: e.target.checked })}
              />
              Send Private DM
            </label>
          )}
          <label className={styles.checkbox}>
            <input
              type="checkbox"
              checked={form.useAiResponse ?? false}
              onChange={(e) => setForm({ ...form, useAiResponse: e.target.checked })}
            />
            Use AI Response
          </label>
        </div>
      </div>

      <div className={styles.footer}>
        <button onClick={onCancel} className={styles.cancelFooterBtn}>
          Cancel
        </button>
        <button onClick={handleSubmit} className={styles.saveBtn} disabled={isSaving || !form.name}>
          <Save className={styles.saveIcon} />
          {form.id ? 'Save Changes' : 'Create Rule'}
        </button>
      </div>
    </div>
  );
}
